// Taxonomy for EXTERNAL (Navitas CAP1/CAP2) concentration limits — the vocabulary the
// "Add new external limit" form exposes and the server validates against. Mirrors the
// compute layer in athena_bfsi_prc.navitas_limit_config + the builder
// (db_ssh_tunnel/create_risk_concentrations_trend.py). Keep the two in lockstep.

export type NavitasDimension =
  | "obligor"
  | "equipment"
  | "industry"
  | "state" // ranking dims
  | "remaining_term"
  | "original_term"
  | "fico"
  | "residual" // numeric attribute dims
  | "credit_grade"
  | "payment_type"
  | "guarantor"
  | "office"; // categorical attribute dims

export type NavitasOperator =
  | "TOP_N"
  | "BOTTOM_N"
  | "RANK_AT"
  | "GT"
  | "GTE"
  | "LT"
  | "LTE"
  | "BETWEEN"
  | "EQ"
  | "NE"
  | "IN"
  | "NOT_IN"
  | "LIKE"
  | "NOT_LIKE"
  | "MEASURE_SUM";

export type NavitasMeasure = "net_investment" | "pv_unguaranteed_residual";

type Kind = "rank" | "numeric" | "categorical";

interface DimSpec {
  value: NavitasDimension;
  label: string;
  kind: Kind;
  column: string; // for reference / hints
  operators: NavitasOperator[];
}

export const NAVITAS_DIMENSIONS: DimSpec[] = [
  {
    value: "obligor",
    label: "Obligor",
    kind: "rank",
    column: "customer_name",
    operators: ["TOP_N", "BOTTOM_N", "RANK_AT"],
  },
  {
    value: "equipment",
    label: "Equipment type",
    kind: "rank",
    column: "asset_type",
    operators: ["TOP_N", "BOTTOM_N", "RANK_AT"],
  },
  {
    value: "industry",
    label: "Industry (SIC, ex-franchise)",
    kind: "rank",
    column: "sic_code",
    operators: ["TOP_N", "BOTTOM_N", "RANK_AT"],
  },
  {
    value: "state",
    label: "State",
    kind: "rank",
    column: "state",
    operators: ["TOP_N", "BOTTOM_N", "RANK_AT"],
  },
  {
    value: "remaining_term",
    label: "Remaining term (mo.)",
    kind: "numeric",
    column: "remaining_months_cash",
    operators: ["GT", "GTE", "LT", "LTE", "BETWEEN", "EQ"],
  },
  {
    value: "original_term",
    label: "Original term (mo.)",
    kind: "numeric",
    column: "term_months",
    operators: ["GT", "GTE", "LT", "LTE", "BETWEEN", "EQ"],
  },
  {
    value: "fico",
    label: "FICO",
    kind: "numeric",
    column: "highest_fico",
    operators: ["GT", "GTE", "LT", "LTE", "BETWEEN", "EQ"],
  },
  {
    value: "residual",
    label: "Residual amount ($)",
    kind: "numeric",
    column: "residual_amount",
    operators: ["GT", "GTE", "LT", "MEASURE_SUM"],
  },
  {
    value: "credit_grade",
    label: "Credit grade",
    kind: "categorical",
    column: "risk_rating",
    operators: ["EQ", "NE", "IN", "NOT_IN", "LIKE", "NOT_LIKE"],
  },
  {
    value: "payment_type",
    label: "Payment type",
    kind: "categorical",
    column: "payment_type",
    operators: ["EQ", "NE", "IN", "NOT_IN"],
  },
  {
    value: "guarantor",
    label: "Personal guarantor",
    kind: "categorical",
    column: "personal_guarantor_flag",
    operators: ["EQ", "NE"],
  },
  {
    value: "office",
    label: "Office (franchise)",
    kind: "categorical",
    column: "office",
    operators: ["IN", "NOT_IN", "EQ", "NE"],
  },
];

/**
 * Both measures a limit can be scored on. **No longer rendered as a choice** — the Add-limit
 * dialog dropped its Measure select and every new external limit is `net_investment`.
 *
 * Kept, with both entries, because the measure itself is not going away: the live limit
 * "Present value of unguaranteed residual" is `pv_unguaranteed_residual` on both books, and
 * `NavitasMeasure` is still the type of `measure` on the proposal body. Deleting the second
 * entry would misrepresent the data rather than simplify it — and the measure decides the
 * headroom regime (a non-net-investment numerator over a net-investment denominator has an
 * unknowable growth factor, so it stays LINEAR).
 */
export const NAVITAS_MEASURES: { value: NavitasMeasure; label: string }[] = [
  { value: "net_investment", label: "Net investment ($)" },
  { value: "pv_unguaranteed_residual", label: "PV of unguaranteed residual ($)" },
];

const OP_LABEL: Record<NavitasOperator, string> = {
  TOP_N: "Top N (largest)",
  BOTTOM_N: "Bottom N (smallest)",
  RANK_AT: "Single at rank N",
  GT: "> greater than",
  GTE: "≥ at least",
  LT: "< less than",
  LTE: "≤ at most",
  BETWEEN: "between (lo, hi)",
  EQ: "= equals",
  NE: "≠ not equal",
  IN: "in list",
  NOT_IN: "not in list",
  LIKE: "matches pattern",
  NOT_LIKE: "does not match pattern",
  MEASURE_SUM: "portfolio total",
};

export function dimSpec(d: NavitasDimension): DimSpec {
  const s = NAVITAS_DIMENSIONS.find((x) => x.value === d);
  if (!s) throw new Error(`unknown dimension ${d}`);
  return s;
}

export function operatorLabel(op: NavitasOperator): string {
  return OP_LABEL[op];
}

export function defaultNavitasOperator(d: NavitasDimension): NavitasOperator {
  return dimSpec(d).operators[0]!;
}

/** Placeholder/hint describing the expected values for a dimension+operator. */
export function navitasValuesHint(d: NavitasDimension, op: NavitasOperator): string {
  if (op === "TOP_N" || op === "BOTTOM_N") return "N — e.g. 3 for the 3 largest/smallest";
  if (op === "RANK_AT") return "rank N — e.g. 1 for the single largest, 4 for 'next outside top 3'";
  if (op === "BETWEEN") return "two values: lo, hi — e.g. 1, 619";
  if (op === "MEASURE_SUM") return "no values — sums the measure across the book";
  if (op === "IN" || op === "NOT_IN") return "comma-separated list — e.g. 12, 15";
  if (op === "LIKE" || op === "NOT_LIKE") return "SQL pattern — e.g. A% for grades starting with A";
  return "a single value — e.g. 90";
}

/** How many values the operator needs; used for client-side validation. */
export function navitasArity(op: NavitasOperator): { min: number; max: number } {
  if (op === "MEASURE_SUM") return { min: 0, max: 0 };
  if (op === "BETWEEN") return { min: 2, max: 2 };
  if (op === "IN" || op === "NOT_IN") return { min: 1, max: 50 };
  return { min: 1, max: 1 };
}
